import type { FieldDef, FieldType, NodeType } from '@dispositio/core';

/** Ein vorgeschlagenes Feld: so, wie es eine Klasse bekommt, wenn man es wählt. */
export interface FieldSuggestion {
  label: string;
  type: FieldType;
  options?: string[];
  /** Schlüssel, unter dem ältere Projekte das Feld schon kennen (z. B. Alter → age) */
  key?: string;
}

const t = (label: string, key?: string): FieldSuggestion => ({ label, type: 'text', ...(key ? { key } : {}) });
const l = (label: string, key?: string): FieldSuggestion => ({ label, type: 'longtext', ...(key ? { key } : {}) });

/** Sinnvolle Felder je vordefinierter Klasse (nach Name); was man öfter braucht, steht vorne. */
export const FIELD_SUGGESTIONS: Record<string, FieldSuggestion[]> = {
  Person: [
    { label: 'Alter', type: 'number', key: 'age' },
    { label: 'Geschlecht', type: 'text', options: ['männlich', 'weiblich', 'sonst'], key: 'gender' },
    { label: 'Rolle', type: 'text', options: ['Hauptfigur', 'Gegenspieler', 'Nebenfigur', 'Statist'] },
    l('Aussehen'),
    t('Herkunft'),
    t('Beruf'),
    l('Ziel'),
    l('Angst'),
    l('Geheimnis'),
    l('Stärken'),
    l('Schwächen'),
    l('Sprechweise'),
    l('Eigenheiten'),
    l('Vorgeschichte'),
    { label: 'Geburtstag', type: 'date' },
  ],
  Ort: [t('Standort', 'location'), l('Atmosphäre'), l('Geräusche und Gerüche'), l('Geschichte', 'history'), l('Bewohner'), l('Bedeutung für die Geschichte'), t('Klima')],
  Landschaft: [l('Atmosphäre'), l('Geräusche und Gerüche'), t('Klima'), l('Pflanzen und Tiere'), l('Bedeutung für die Geschichte')],
  Gegenstand: [l('Aussehen'), l('Funktion'), t('Herkunft'), t('Besitzer'), l('Bedeutung für die Geschichte'), t('Wert')],
  Organisation: [t('Art', 'kind'), l('Ziel'), l('Struktur'), l('Mitglieder'), t('Sitz'), l('Geschichte'), l('Ruf')],
  Konzept: [l('Erklärung'), l('Regeln'), l('Wer weiß davon?'), t('Herkunft'), l('Bedeutung für die Geschichte')],
  Welt: [l('Technik und Magie'), l('Politik und Macht'), l('Kulturen'), l('Geographie'), l('Geschichte'), l('Religion und Glaube'), l('Wirtschaft')],
  Prämisse: [l('Genre'), l('Zielgruppe'), l('Ton'), l('Vergleichstitel')],
};

/** Vorschläge für eine Klasse, ohne die Felder, die sie schon hat (nach Namen und Schlüssel). */
export function suggestionsFor(type: Pick<NodeType, 'name' | 'fields'>): FieldSuggestion[] {
  const labels = new Set(type.fields.map((f) => f.label.trim().toLowerCase()));
  const keys = new Set(type.fields.map((f) => f.key));
  return (FIELD_SUGGESTIONS[type.name] ?? []).filter((s) => !labels.has(s.label.toLowerCase()) && !(s.key && keys.has(s.key)));
}

const TRANSLIT: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/** Schlüssel für ein neues Feld aus seinem Namen („Wer weiß davon?“ → „wer_weiss_davon“), eindeutig in der Klasse. */
export function fieldKeyFor(label: string, taken: ReadonlySet<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[äöüß]/g, (c) => TRANSLIT[c]!)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 36) || 'feld';
  const root = /^[a-z]/.test(base) ? base : `f_${base}`.slice(0, 36);
  if (!taken.has(root)) return root;
  for (let i = 2; ; i++) if (!taken.has(`${root}_${i}`)) return `${root}_${i}`;
}

/** Felddefinition zu einem Vorschlag oder einem selbst entworfenen Feld (mit eindeutigem Schlüssel). */
export function newFieldDef(type: Pick<NodeType, 'fields'>, field: FieldSuggestion): FieldDef {
  const taken = new Set(type.fields.map((f) => f.key));
  const key = field.key && !taken.has(field.key) ? field.key : fieldKeyFor(field.label, taken);
  return { key, label: field.label.trim(), type: field.type, ...(field.options && field.options.length >= 2 ? { options: field.options } : {}) };
}

/** Feste Felder einer Klasse: Titel, Hover-Text, Bild und Verweisfelder lassen sich nicht entfernen. */
export const isCoreField = (type: Pick<NodeType, 'titleKey' | 'hoverKey' | 'imageKey'>, def: FieldDef): boolean =>
  def.key === type.titleKey || def.key === type.hoverKey || def.key === type.imageKey || def.type === 'ref';
